import { z } from 'zod';
import { Role } from '../enums';
import { emailSchema, nameSchema, passwordSchema, uuidSchema } from './common';
/**
 * Sign in.
 *
 * There is no `rememberMe`. It used to be here, and the checkbox that fed it was doing
 * harm rather than good: unticked — the default — it issued a session a quarter the
 * length of the ticked one, so the team's actual experience of the console was being
 * signed out mid-week by a control most of them never noticed. A console that a fixed,
 * invited team signs into from their own machines has one sensible session length, and it
 * is now the configured one (`JWT_REFRESH_TTL`) for everybody. See `TokenService`.
 */
export const loginSchema = z.object({
    email: emailSchema,
    // Deliberately NOT `passwordSchema`: the policy applies when *setting* a
    // password. Applying it at login would reject legacy passwords with a
    // validation error that reveals the policy before authentication.
    password: z.string().min(1, 'Enter your password.'),
});
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
})
    .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    error: 'Passwords do not match.',
})
    .refine((data) => data.newPassword !== data.currentPassword, {
    path: ['newPassword'],
    error: 'Choose a password you have not used before.',
});
/**
 * There is no public sign-up. The client operates with a small fixed team, so
 * accounts are created by invitation only — an open registration endpoint would
 * be pure attack surface with no user.
 */
export const inviteUserSchema = z.object({
    email: emailSchema,
    name: nameSchema,
    role: z.enum([Role.ADMIN, Role.EDITOR, Role.AUTHOR]),
});
export const acceptInviteSchema = z
    .object({
    token: z.string().min(20, 'This invitation link is malformed.'),
    password: passwordSchema,
    confirmPassword: z.string(),
})
    .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    error: 'Passwords do not match.',
});
export const sessionUserSchema = z.object({
    id: uuidSchema,
    email: z.email(),
    name: z.string(),
    role: z.enum(Role),
    avatarMediaId: uuidSchema.nullable(),
    mustChangePassword: z.boolean(),
    permissions: z.array(z.string()),
});
//# sourceMappingURL=auth.js.map